import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import EmployerUser from "../../apiResources/EmployerUser";
import SettingLabel from "./SettingLabel";
import Skeleton from "../../components/library/Skeleton";

const JobTitleSettingOption = (props: Props) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          jobTitle: user.errors.jobTitle?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const value = user?.jobTitle;
  const showNotSpecified = !isLoading && !value;
  const showValue = !isLoading && Boolean(value);

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Job title</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="22" />}
        {showNotSpecified && <div>Not specified</div>}
        {showValue && <div>{value}</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    jobTitle: user.jobTitle ?? "",
  };

  const validation = Yup.object({
    jobTitle: Yup.string().required("Please enter your job title"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Job title</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <TextInputField label="Job Title" name="jobTitle" hiddenLabel />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props = {
  user: EmployerUser;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: EmployerUser) => void;
};

type FormValues = {
  jobTitle: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default JobTitleSettingOption;
